
import { useState, useEffect } from 'react';
import axios from '../../axios';
import API_BASE_URL from '../../config';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';

export default function PaymentMethodsPage() {
  const companyId = parseInt(localStorage.getItem('companyId'), 10);

  const [methods, setMethods] = useState([]);
  const [form, setForm] = useState({
    id:     null,
    code:   '',
    nameAr: '',
    nameEn: '',
    paymentType: 1, // Default to Cash
    accountId: ''
  });

  // Fetch payment methods on mount
  useEffect(() => {
    fetchMethods();
    fetchAccounts(); // Need accounts for dropdown
  }, []);

  const [accounts, setAccounts] = useState([]);

  const fetchAccounts = () => {
     // Correct endpoint: /ChartOfAccounts/postable
     // The backend gets companyId from the token
     axios.get(`/ChartOfAccounts/postable`)
        .then(res => setAccounts(res.data || []))
        .catch(console.error);
  };

  const fetchMethods = () => {
    axios
      .get(`/PaymentMethod/by-company/${companyId}`)
      .then(res => setMethods(res.data))
      .catch(console.error);
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    const payload = {
      id: form.id || 0,
      companyId,
      code:   form.code,
      nameAr: form.nameAr,
      nameEn: form.nameEn,
      paymentType: parseInt(form.paymentType),
      accountId: form.accountId ? form.accountId : null,
      isActive: true
    };

    try {
      if (form.id) {
        await axios.put(`/PaymentMethod/${form.id}`, payload);
      } else {
        await axios.post('/PaymentMethod', payload);
      }
      setForm({ id: null, code: '', nameAr: '', nameEn: '', paymentType: 1, accountId: '' });
      fetchMethods();
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء الحفظ');
    }
  };

  const handleEdit = method => {
    setForm({
      id:     method.id,
      code:   method.code,
      nameAr: method.nameAr,
      nameEn: method.nameEn || '',
      paymentType: method.paymentType || 1,
      accountId: method.accountId || ''
    });
  };

  const handleDelete = async id => {
    if (!window.confirm('هل أنت متأكد من حذف طريقة الدفع هذه؟')) return;
    try {
      await axios.delete(`/PaymentMethod/${id}`);
      fetchMethods();
    } catch (err) {
      console.error(err);
      alert('حدث خطأ أثناء الحذف');
    }
  };

  return (
    <Card dir="rtl" className="max-w-2xl mx-auto mt-10">
      <CardContent>
        <h2 className="text-xl font-bold mb-4">إدارة طرق الدفع</h2>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block mb-1">الرمز (Code)</label>
              <Input
                name="code"
                value={form.code}
                onChange={handleChange}
                placeholder="مثال: CASH"
              />
            </div>
            <div>
              <label className="block mb-1">الاسم بالعربية</label>
              <Input
                name="nameAr"
                value={form.nameAr}
                onChange={handleChange}
                placeholder="مثال: نقداً"
              />
            </div>
            <div>
              <label className="block mb-1">الاسم بالإنجليزية</label>
              <Input
                name="nameEn"
                value={form.nameEn}
                onChange={handleChange}
                placeholder="مثال: Cash"
              />
            </div>
            
            {/* Payment Type Select */}
            <div>
              <label className="block mb-1">نوع طريقة الدفع</label>
              <select
                name="paymentType"
                value={form.paymentType}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              >
                <option value="1">نقدي (Cash)</option>
                <option value="2">تحويل بنكي (Bank Transfer)</option>
                <option value="3">شبكة / بطاقة (Card)</option>
                <option value="4">آجل (Credit)</option>
              </select>
            </div>

            {/* Account Select */}
            <div>
              <label className="block mb-1">الحساب المحاسبي المرتبط</label>
              <select
                name="accountId"
                value={form.accountId}
                onChange={handleChange}
                className="w-full border p-2 rounded"
              >
                <option value="">-- اختر الحساب --</option>
                {accounts.map(acc => (
                  <option key={acc.id} value={acc.id}>
                    {acc.code} - {acc.nameAr}
                  </option>
                ))}
              </select>
            </div>

          </div>
          <div className="flex justify-end gap-2">
            {form.id && (
              <Button
                variant="outline"
                onClick={() => setForm({ id: null, code: '', nameAr: '', nameEn: '', paymentType: 1, accountId: '' })}
              >
                إلغاء
              </Button>
            )}
            <Button type="submit">
              {form.id ? 'تحديث' : 'إضافة'} طريقة دفع
            </Button>
          </div>
        </form>

        {/* List */}
        <table className="w-full table-auto border-collapse text-sm">
          <thead>
            <tr className="bg-gray-100">
              {['#', 'الرمز', 'الاسم عربي', 'النوع', 'الحساب المرتبط', 'إجراءات'].map((h, i) => (
                <th key={i} className="px-3 py-2 border text-center">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {methods.map((m, idx) => {
              const pTypes = {1: 'نقدي', 2: 'تحويل', 3: 'شبكة', 4: 'آجل'};
              const accName = accounts.find(a => a.id === m.accountId)?.nameAr || '-';
              
              return (
              <tr key={m.id}>
                <td className="px-3 py-2 border text-center">{idx + 1}</td>
                <td className="px-3 py-2 border text-center">{m.code}</td>
                <td className="px-3 py-2 border text-center">{m.nameAr}</td>
                <td className="px-3 py-2 border text-center">{pTypes[m.paymentType] || m.paymentType}</td>
                <td className="px-3 py-2 border text-center">{accName}</td>
                <td className="px-3 py-2 border text-center space-x-2">
                  <Button size="icon" onClick={() => handleEdit(m)}>
                    🖉
                  </Button>
                  <Button
                    size="icon"
                    variant="outline"
                    onClick={() => handleDelete(m.id)}
                  >
                    🗑️
                  </Button>
                </td>
              </tr>
              );
            })}
            {methods.length === 0 && (
              <tr>
                <td colSpan={5} className="py-4 text-center text-gray-500">
                  لا توجد طرق دفع
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </CardContent>
    </Card>
  );
}
